'use client'

import { useEffect, useState, useRef } from 'react'
import styles from './ScrollToTop.module.css'

export default function ScrollToTop() {
  const [isVisible, setIsVisible] = useState(false)
  const pathRef = useRef<SVGPathElement>(null)
  const isAnimatingRef = useRef(false)

  // The SVG circle's circumference (for a circle of radius ≈49)
  const circumference = 307.919

  // Handle scroll events to update visibility and progress circle
  useEffect(() => {
    const updateScroll = () => {
      if (isAnimatingRef.current) return

      const scrollTop = window.scrollY
      const shouldBeVisible = scrollTop > 300

      if (shouldBeVisible !== isVisible) {
        setIsVisible(shouldBeVisible)
      }

      if (shouldBeVisible && pathRef.current) {
        const docHeight = document.documentElement.scrollHeight - window.innerHeight
        const scrollPercent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0
        const dashOffset = circumference - (circumference * scrollPercent) / 100
        pathRef.current.style.strokeDashoffset = dashOffset.toString()
      }
    }

    window.addEventListener('scroll', updateScroll)
    updateScroll() // Initial call

    return () => window.removeEventListener('scroll', updateScroll)
  }, [isVisible, circumference])

  // Simple scroll to top function using the browser's native smooth scrolling
  const scrollToTop = () => {
    // Use native smooth scrolling
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  if (!isVisible) return null

  return (
    <div className="fixed right-8 bottom-8 z-[99]">
      <div
        onClick={scrollToTop}
        aria-label="scroll to top"
        className={`${styles['oc-progress-wrap']} ${styles['active-progress']} cursor-pointer`}
      >
        <svg
          className={styles['oc-progress-circle']}
          width="100%"
          height="100%"
          viewBox="-1 -1 102 102"
        >
          <path
            ref={pathRef}
            d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98"
            style={{
              stroke: '#ff4500',
              strokeWidth: 4,
              fill: 'none',
              strokeDasharray: circumference,
              strokeDashoffset: circumference,
            }}
          />
        </svg>
        <span className={styles['oc-progress-arrow']}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10 15C10.1658 15 10.3247 14.9342 10.4419 14.8169C10.5592 14.6997 10.625 14.5408 10.625 14.375V7.13375L13.3075 9.8175C13.3656 9.87561 13.4346 9.92171 13.5105 9.95316C13.5864 9.98461 13.6678 10.0008 13.75 10.0008C13.8322 10.0008 13.9136 9.98461 13.9895 9.95316C14.0654 9.92171 14.1344 9.87561 14.1925 9.8175C14.2506 9.75939 14.2967 9.69041 14.3282 9.61448C14.3596 9.53856 14.3758 9.45718 14.3758 9.375C14.3758 9.29282 14.3596 9.21145 14.3282 9.13552C14.2967 9.0596 14.2506 8.99061 14.1925 8.9325L10.4425 5.1825C10.3844 5.1243 10.3155 5.07812 10.2395 5.04661C10.1636 5.0151 10.0822 4.99889 10 4.99889C9.91779 4.99889 9.83639 5.0151 9.76046 5.04661C9.68453 5.07812 9.61556 5.1243 9.5575 5.1825L5.8075 8.9325C5.69014 9.04986 5.62421 9.20903 5.62421 9.375C5.62421 9.54097 5.69014 9.70015 5.8075 9.8175C5.92486 9.93486 6.08403 10.0008 6.25 10.0008C6.41597 10.0008 6.57514 9.93486 6.6925 9.8175L9.375 7.13375V14.375C9.375 14.5408 9.44085 14.6997 9.55806 14.8169C9.67527 14.9342 9.83424 15 10 15Z"
              fill="black"
            />
          </svg>
        </span>
      </div>
    </div>
  )
}
